import { Check, CheckCheck, Volume2, VolumeX } from 'lucide-react';
import { NotificationsIcon } from '@/shared/config';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Popover } from '@base-ui-components/react/popover';
import { cn } from '@/shared/lib/cn';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { formatDateTime, formatRelativeTime } from '@/shared/lib/date';
import styles from './NotificationBell.module.css';
import { useNotificationsStore } from '../model/notifications-store';
import { notificationsCache, useNotificationsPopup, useUnreadCount } from '../api/queries';
import { useNotificationSoundStore } from '../model/notification-sound-store';
import { playPreviewSound } from '../lib/notification-sounds';
import { notificationLink } from '../lib/notification-link';
import { formatNotificationTitle } from '../lib/notification-title';
import { NotificationEventIcon, notificationVisual } from '../lib/notification-icon';
import { useAuthStore } from '@/features/auth';
import { EmptyState, IconButton, Scroller, Skeleton, UnreadBadge } from '@/shared/ui';

const PAGE_GUTTER = 12;

const ARROW_H = 10;

const ISLAND_OFFSET = 12 + 6 + ARROW_H;
const ROW_OFFSET = 8 + ARROW_H;

const ARROW_ALIGN = -12;

export const NotificationBell: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [open, setOpen] = useState(false);
  const { data: items = [], isPending } = useNotificationsPopup(open);
  const { data: unreadCount = 0 } = useUnreadCount(isAuthenticated && !!user?.id);
  const markAsRead = useNotificationsStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationsStore((s) => s.markAllAsRead);
  const soundEnabled = useNotificationSoundStore((s) => s.enabled);
  const setSoundEnabled = useNotificationSoundStore((s) => s.setEnabled);

  const handleToggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    if (next) playPreviewSound(useNotificationSoundStore.getState().volume);
  };

  const narrow = useMediaQuery('(max-width: 640px)');
  const island = useMediaQuery('(max-width: 1100px)');

  const handlePrefetch = () => {
    if (isAuthenticated) notificationsCache.prefetchPopup();
  };

  if (!isAuthenticated) return null;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        render={
          <button
            type="button"
            className={styles.trigger}
            onPointerEnter={handlePrefetch}
            onFocus={handlePrefetch}
            aria-label={`Уведомления${unreadCount ? ` (${unreadCount} новых)` : ''}`}
          />
        }
      >
        <NotificationsIcon size={18} aria-hidden />
        <UnreadBadge count={unreadCount} className={styles.badge} aria-hidden />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner
          side="bottom"
          align="end"
          sideOffset={island ? ISLAND_OFFSET : ROW_OFFSET}
          alignOffset={narrow ? 0 : ARROW_ALIGN}
          arrowPadding={14}
          collisionPadding={narrow ? { left: PAGE_GUTTER, right: PAGE_GUTTER } : 0}
          className={styles.positioner}
        >
          <Popover.Popup className={styles.content} aria-label="Уведомления">
            { }
            <Popover.Arrow className={styles.arrow}>
              <svg viewBox="0 0 24 10" aria-hidden>
                <path d="M0 11H24V10C19.8 10 17.4 8.9 15.2 4.9L13.9 2.6Q12 -0.6 10.1 2.6L8.8 4.9C6.6 8.9 4.2 10 0 10Z" />
              </svg>
            </Popover.Arrow>
          <div className={styles.header}>
            <span className={styles.headerTitle}>
              Уведомления
              {unreadCount > 0 && (
                <span className={styles.headerCount}>{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </span>
            <span className={styles.headerActions}>
              <IconButton
                size="sm"
                label={soundEnabled ? 'Выключить звук уведомлений' : 'Включить звук уведомлений'}
                tooltip={soundEnabled ? 'Выключить звук' : 'Включить звук'}
                onClick={handleToggleSound}
              >
                {soundEnabled ? <Volume2 size={16} aria-hidden /> : <VolumeX size={16} aria-hidden />}
              </IconButton>
              {unreadCount > 0 && (
                <IconButton size="sm" label="Прочитать все" onClick={() => void markAllAsRead()}>
                  <CheckCheck size={16} aria-hidden />
                </IconButton>
              )}
            </span>
          </div>

          {isPending ? (
            <div className={styles.skeleton} aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className={styles.skeletonRow}>
                  <Skeleton width={16} height={16} radius="var(--radius-sm)" />
                  <Skeleton height={12} width={i === 1 ? '60%' : '82%'} />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState compact title="Всё прочитано" className={styles.empty} />
          ) : (
            <Scroller className={styles.list}>
              {items.map((n) => {
                const { tone, label } = notificationVisual(n.eventType, n.payload);
                return (
                  <div key={n.id} className={styles.row}>
                    <Link
                      to={notificationLink(n, user?.id)}
                      className={styles.item}
                      onClick={() => {
                        if (!n.readAt && !n.read) markAsRead(n.id);
                        setOpen(false);
                      }}
                    >
                      <span className={cn(styles.eventIcon, styles[`tone-${tone}`])} title={label}>
                        <NotificationEventIcon eventType={n.eventType} payload={n.payload} />
                      </span>
                      <span className={styles.itemTitle} title={formatNotificationTitle(n)}>{formatNotificationTitle(n)}</span>
                      <time className={styles.itemTime} dateTime={n.createdAt} title={formatDateTime(n.createdAt)}>
                        {formatRelativeTime(n.createdAt)}
                      </time>
                    </Link>
                    <IconButton
                      size="sm"
                      className={styles.readBtn}
                      label="Отметить прочитанным"
                      tooltip="Прочитано, не открывая"
                      onClick={() => markAsRead(n.id)}
                    >
                      <Check size={14} aria-hidden />
                    </IconButton>
                  </div>
                );
              })}
            </Scroller>
          )}

          <Link to="/notifications" className={styles.allLink} onClick={() => setOpen(false)}>
            Все уведомления
          </Link>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
};
